import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import RSVPForm from './RSVPForm';
import { Calendar, MapPin, Clock } from 'lucide-react';

export default function NoirVinyl({
  data = {},
  onSubmitRSVP,
  isSubmitting,
  submitSuccess,
  isPreview = false
}) {
  const {
    couple_names = 'Sarah & Michael',
    event_date = '',
    event_time = '',
    venue_name = '',
    venue_address = '',
    story_text = '',
    timeline = [],
    hero_image_url = '',
    hero_photos = [],
    color_theme = {},
    font_style = 'serif',
    background_music_url = '',
    song_title = '',
    artist_name = '',
    vinyl_cover_image = ''
  } = data || {};

  const bgColor = color_theme.background || '#000000';
  const primaryColor = color_theme.primary || '#ffffff';
  const secondaryColor = color_theme.secondary || '#888888';
  const accentColor = color_theme.accent || '#d4af37';

  // Helper: convert hex color to rgba string
  const rgba = (hex, alpha) => {
    const h = (hex || '#ffffff').replace('#', '');
    const r = parseInt(h.substring(0, 2), 16) || 255;
    const g = parseInt(h.substring(2, 4), 16) || 255;
    const b = parseInt(h.substring(4, 6), 16) || 255;
    return `rgba(${r},${g},${b},${alpha})`;
  };

  const getMainPhoto = () => hero_photos?.[0] || hero_image_url || '';
  const mainPhoto = getMainPhoto();
  const bgImage = data.hero_bg_image || mainPhoto;

  // Background image customization
  const bgBlur = data.bg_blur ?? 5;
  const bgOverlay = data.bg_overlay ?? 10;
  const bgOversize = data.bg_oversize ?? 25;
  const bgHeight = data.bg_height ?? 480;
  const fullPageBg = data.full_page_bg ?? false;
  const heroContentY = data.hero_content_y ?? 0;
  const frameSize = data.frame_size ?? 320;
  const frameBorder = data.frame_border ?? 3;
  const frameBorderColor = data.frame_border_color || 'rgba(255,255,255,0.2)';
  const vinylColor = data.vinyl_color || '';

  const fontClass = font_style === 'sans'
    ? 'font-sans'
    : font_style === 'script'
    ? "font-['Playfair_Display',serif]"
    : 'font-serif';

  // Audio state
  const audioRef = useRef(null);
  const fadeRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPause = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('ended', onEnded);
      if (fadeRef.current) clearInterval(fadeRef.current);
    };
  }, []);

  // Auto-play on first user interaction (scroll, tap, click)
  const hasAutoPlayed = useRef(false);

  useEffect(() => {
    if (isPreview || !background_music_url) return;

    const autoStart = () => {
      if (hasAutoPlayed.current) return;
      hasAutoPlayed.current = true;
      const audio = audioRef.current;
      if (!audio || !audio.paused) return;
      audio.volume = 0;
      audio.play().then(() => {
        let vol = 0;
        fadeRef.current = setInterval(() => {
          vol = Math.min(vol + 0.04, 1);
          audio.volume = vol;
          if (vol >= 1) clearInterval(fadeRef.current);
        }, 100);
      }).catch(() => {});
      // Clean up listeners after first trigger
      document.removeEventListener('click', autoStart);
      document.removeEventListener('touchstart', autoStart);
      document.removeEventListener('scroll', autoStart);
    };

    document.addEventListener('click', autoStart, { once: true });
    document.addEventListener('touchstart', autoStart, { once: true });
    document.addEventListener('scroll', autoStart, { once: true });

    return () => {
      document.removeEventListener('click', autoStart);
      document.removeEventListener('touchstart', autoStart);
      document.removeEventListener('scroll', autoStart);
    };
  }, [isPreview, background_music_url]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (audio.paused) {
        audio.volume = 0;
        await audio.play();
        let vol = 0;
        fadeRef.current = setInterval(() => {
          vol = Math.min(vol + 0.05, 1);
          audio.volume = vol;
          if (vol >= 1) clearInterval(fadeRef.current);
        }, 80);
      } else {
        audio.pause();
      }
    } catch (e) {
      console.error('Audio error:', e);
    }
  }, []);

  // Equalizer bar heights — randomized per beat cycle
  const eqBars = [4, 8, 6, 12, 9, 14, 7, 11, 5, 13, 8, 10, 6];

  return (
    <div
      className={`min-h-screen w-full ${fontClass} relative overflow-hidden`}
      style={{ backgroundColor: bgColor, color: primaryColor }}
    >
      {/* Full-page background image (when toggle is on) */}
      {fullPageBg && bgImage && (
        <>
          <img
            src={bgImage}
            alt=""
            draggable={false}
            className="absolute"
            style={{
              top: `-${Math.ceil(bgOversize / 2)}px`,
              left: `-${Math.ceil(bgOversize / 2)}px`,
              width: `calc(100% + ${bgOversize}px)`,
              height: `calc(100% + ${bgOversize}px)`,
              objectFit: 'cover',
              zIndex: 0,
              filter: bgBlur > 0 ? `blur(${bgBlur}px)` : 'none',
            }}
          />
          {bgOverlay > 0 && (
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${bgOverlay / 100})`, zIndex: 1 }} />
          )}
        </>
      )}
      <div className="relative" style={{ zIndex: 2 }}>
      {/* Equalizer keyframe animation */}
      <style>{`
        @keyframes eqBounce {
          0% { transform: scaleY(0.25); }
          25% { transform: scaleY(0.9); }
          50% { transform: scaleY(0.4); }
          75% { transform: scaleY(1); }
          100% { transform: scaleY(0.25); }
        }
      `}</style>

      {/* ===== HERO: Album Sleeve Style ===== */}
      <div
        className="relative w-full overflow-hidden"
        style={{ minHeight: `${bgHeight}px`, backgroundColor: fullPageBg ? 'transparent' : bgColor }}
      >
        {/* Hero-only background (when full page bg is off) */}
        {!fullPageBg && bgImage && (
          <img
            src={bgImage}
            alt=""
            draggable={false}
            style={{
              position: 'absolute',
              top: `-${Math.ceil(bgOversize / 2)}px`,
              left: `-${Math.ceil(bgOversize / 2)}px`,
              width: `calc(100% + ${bgOversize}px)`,
              height: `calc(100% + ${bgOversize}px)`,
              objectFit: 'cover',
              zIndex: 0,
              filter: bgBlur > 0 ? `blur(${bgBlur}px)` : 'none',
            }}
          />
        )}
        {!fullPageBg && bgOverlay > 0 && (
          <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${bgOverlay / 100})`, zIndex: 1 }} />
        )}

        {/* Album sleeve content */}
        <div className="relative z-10 flex flex-col items-center px-6 pt-10 pb-8" style={{ transform: `translateY(${heroContentY}px)` }}>
          {/* Framed photo with vinyl overlay */}
          <div className="relative" style={{ width: `${frameSize}px`, margin: '0 auto' }}>
            <div
              className="relative overflow-hidden"
              style={{ aspectRatio: '1/1', border: `${frameBorder}px solid ${frameBorderColor}` }}
            >
              {/* B&W photo */}
              {mainPhoto ? (
                <img
                  src={mainPhoto}
                  alt={couple_names}
                  className="w-full h-full object-cover"
                  style={{ filter: 'grayscale(100%) contrast(1.1)' }}
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full bg-neutral-900 flex items-center justify-center">
                  <p className="text-sm" style={{ color: rgba(primaryColor, 0.2) }}>Upload a photo</p>
                </div>
              )}

              {/* Animated Equalizer bars — bottom left */}
              <div className="absolute bottom-3 left-3 flex items-end gap-[2px] z-20">
                {eqBars.map((h, i) => (
                  <div
                    key={i}
                    className="rounded-sm origin-bottom"
                    style={{
                      width: '3px',
                      height: `${h}px`,
                      backgroundColor: rgba(primaryColor, 0.6),
                      ...(isPlaying ? {
                        animation: `eqBounce ${0.6 + (i % 5) * 0.2}s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
                        animationDelay: `${i * 0.08}s`,
                      } : {
                        transform: 'scaleY(0.3)',
                        transition: 'transform 0.4s ease',
                      }),
                    }}
                  />
                ))}
              </div>

              {/* Song title overlay at bottom of frame */}
              {(song_title || artist_name) && (
                <div className="absolute bottom-0 left-0 right-0 z-20 px-4 pb-4 pt-12 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                  <p
                    className="text-lg font-light tracking-wide"
                    style={{ color: primaryColor, fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {song_title || 'Our Song'}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: rgba(primaryColor, 0.6) }}>
                    {artist_name || 'Artist'}
                  </p>
                </div>
              )}
            </div>

            {/* ===== Vinyl Record — anchored to frame top-right, NOT clipped ===== */}
            {background_music_url && (
              <div
                className="absolute z-20 cursor-pointer group"
                style={{ top: '5%', right: '-15px', width: '130px' }}
                onClick={togglePlay}
                data-testid="noir-vinyl-disc"
              >
                <div className="relative" style={{ paddingBottom: '100%' }}>
                  <motion.div
                    animate={{ rotate: isPlaying ? 360 : 0 }}
                    transition={{
                      duration: 4,
                      ease: 'linear',
                      repeat: isPlaying ? Infinity : 0,
                    }}
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: vinylColor
                        ? `radial-gradient(circle at center, ${vinylColor} 0%, ${vinylColor}cc 50%, ${vinylColor}99 70%, ${vinylColor}66 100%)`
                        : 'radial-gradient(circle at center, #2f2f2f 0%, #171717 50%, #0d0d0d 70%, #000 100%)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
                      border: '2px solid rgba(255,255,255,0.15)',
                    }}
                  >
                    {[10, 18, 26, 34].map((inset) => (
                      <div
                        key={inset}
                        className="absolute rounded-full border border-white/[0.08]"
                        style={{ inset: `${inset}%` }}
                      />
                    ))}
                    <div
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full overflow-hidden border border-white/20"
                      style={{ width: '52%', height: '52%' }}
                    >
                      {(vinyl_cover_image || mainPhoto) ? (
                        <img
                          src={vinyl_cover_image || mainPhoto}
                          alt=""
                          className="w-full h-full object-cover"
                          style={{ filter: 'grayscale(100%)' }}
                          draggable={false}
                        />
                      ) : (
                        <div className="w-full h-full bg-neutral-700" />
                      )}
                    </div>
                    <div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black border border-white/30 z-10" />
                    <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#fff' }}>
                        {isPlaying ? (
                          <span className="flex gap-0.5">
                            <span className="block h-4 w-[3px] rounded bg-white" />
                            <span className="block h-4 w-[3px] rounded bg-white" />
                          </span>
                        ) : (
                          <span
                            className="ml-0.5 block h-0 w-0"
                            style={{
                              borderTop: '8px solid transparent',
                              borderBottom: '8px solid transparent',
                              borderLeft: '12px solid white',
                            }}
                          />
                        )}
                      </span>
                    </div>
                  </motion.div>

                  <motion.div
                    animate={{ rotate: isPlaying ? 12 : 0 }}
                    transition={{ duration: 0.6, ease: 'easeInOut' }}
                    className="absolute z-30"
                    style={{
                      right: '-12%',
                      top: '-10%',
                      width: '40%',
                      height: '65%',
                      transformOrigin: '75% 8%',
                    }}
                  >
                    <img
                      src="/assets/tonearm.png"
                      alt=""
                      className="w-full h-full"
                      style={{ objectFit: 'contain' }}
                      draggable={false}
                    />
                  </motion.div>
                </div>
              </div>
            )}
          </div>

          {/* Couple names below the frame */}
          <div className="text-center mt-8">
            <p
              className="text-xs tracking-[0.3em] uppercase"
              style={{ color: accentColor }}
            >
              Together with their families
            </p>
            <h1
              className="mt-3 text-3xl font-light tracking-wide"
              style={{ color: primaryColor, fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {couple_names}
            </h1>
            <p className="mt-2 text-sm" style={{ color: rgba(primaryColor, 0.5) }}>
              Request the pleasure of your company
            </p>
          </div>
        </div>
      </div>

      {/* Hidden audio element */}
      {background_music_url && (
        <audio ref={audioRef} preload="metadata">
          <source src={background_music_url} type="audio/mpeg" />
        </audio>
      )}

      {/* ===== CONTENT SECTIONS WITH OPTIONAL BACKGROUND ===== */}
      <div className="relative" style={{ backgroundColor: fullPageBg ? 'transparent' : bgColor }}>
        {!fullPageBg && data.content_bg_image && (
          <>
            <img
              src={data.content_bg_image}
              alt=""
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ zIndex: 0 }}
            />
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${bgOverlay / 100})`, zIndex: 1 }} />
          </>
        )}
        <div className="relative" style={{ zIndex: 2 }}>

      {/* ===== WEDDING DETAILS ===== */}
      <div className="px-6 py-12 text-center">
        <div className="flex items-center justify-center gap-8 mb-6">
          <div className="h-px flex-1 max-w-[60px]" style={{ backgroundColor: `${accentColor}40` }} />
          <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: accentColor }}>
            Wedding Ceremony
          </p>
          <div className="h-px flex-1 max-w-[60px]" style={{ backgroundColor: `${accentColor}40` }} />
        </div>

        <div className="space-y-4">
          {event_date && (
            <div className="flex items-center justify-center gap-2">
              <Calendar className="w-3.5 h-3.5" style={{ color: accentColor }} />
              <span className="text-sm" style={{ color: rgba(primaryColor, 0.8) }}>{event_date}</span>
            </div>
          )}
          {event_time && (
            <div className="flex items-center justify-center gap-2">
              <Clock className="w-3.5 h-3.5" style={{ color: accentColor }} />
              <span className="text-sm" style={{ color: rgba(primaryColor, 0.8) }}>{event_time}</span>
            </div>
          )}
          {venue_name && (
            <div className="flex items-center justify-center gap-2">
              <MapPin className="w-3.5 h-3.5" style={{ color: accentColor }} />
              <div>
                <span className="text-sm" style={{ color: rgba(primaryColor, 0.8) }}>{venue_name}</span>
                {venue_address && (
                  <p className="text-xs mt-0.5" style={{ color: rgba(primaryColor, 0.4) }}>{venue_address}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== OUR STORY ===== */}
      {story_text && (
        <div className="px-6 py-10">
          <div className="text-center mb-6">
            <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: accentColor }}>
              Our Story
            </p>
          </div>
          <p
            className="text-sm leading-relaxed text-center max-w-[320px] mx-auto"
            style={{ color: rgba(primaryColor, 0.65), fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {story_text}
          </p>
        </div>
      )}

      {/* ===== TIMELINE ===== */}
      {timeline && timeline.length > 0 && (
        <div className="px-6 py-10">
          <div className="text-center mb-8">
            <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: accentColor }}>
              Timeline
            </p>
          </div>
          <div className="max-w-[300px] mx-auto space-y-6">
            {timeline.map((item, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: accentColor }}
                  />
                  {idx < timeline.length - 1 && (
                    <div className="w-px flex-1 mt-1" style={{ backgroundColor: `${accentColor}30` }} />
                  )}
                </div>
                <div className="pb-4">
                  {item.time && (
                    <p className="text-xs" style={{ color: accentColor }}>{item.time}</p>
                  )}
                  <p className="text-sm font-medium" style={{ color: rgba(primaryColor, 0.8) }}>{item.title}</p>
                  {item.description && (
                    <p className="text-xs mt-0.5" style={{ color: rgba(primaryColor, 0.4) }}>{item.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== RSVP SECTION ===== */}
      <div className="px-6 py-12">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-2">
            <div className="h-px flex-1 max-w-[40px]" style={{ backgroundColor: `${accentColor}40` }} />
            <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: accentColor }}>
              RSVP
            </p>
            <div className="h-px flex-1 max-w-[40px]" style={{ backgroundColor: `${accentColor}40` }} />
          </div>
          <p className="text-xs mt-1" style={{ color: rgba(primaryColor, 0.4) }}>Kindly respond by your earliest convenience</p>
        </div>

        {!isPreview && (
          <div
            className="rounded-lg p-5 max-w-[360px] mx-auto"
            style={{ backgroundColor: rgba(primaryColor, 0.05), border: `1px solid ${rgba(primaryColor, 0.1)}` }}
          >
            <RSVPForm
              onSubmit={onSubmitRSVP}
              colorTheme={{ ...color_theme, background: bgColor }}
              fontStyle={font_style}
              isSubmitting={isSubmitting}
              submitSuccess={submitSuccess}
            />
          </div>
        )}

        {isPreview && (
          <div
            className="rounded-lg p-5 text-center max-w-[360px] mx-auto"
            style={{ backgroundColor: rgba(primaryColor, 0.05), border: `1px solid ${rgba(primaryColor, 0.1)}` }}
          >
            <p className="text-sm" style={{ color: rgba(primaryColor, 0.3) }}>RSVP form will appear here</p>
          </div>
        )}
      </div>

      {/* ===== FOOTER ===== */}
      <div className="text-center pb-10 pt-4">
        <div className="w-8 h-px mx-auto mb-4" style={{ backgroundColor: `${accentColor}40` }} />
        <p className="text-[10px] tracking-wider" style={{ color: rgba(primaryColor, 0.25) }}>
          We can't wait to celebrate with you
        </p>
      </div>

        </div>{/* end relative z-2 */}
      </div>{/* end content bg wrapper */}
      </div>{/* end relative z-2 for full page */}
    </div>
  );
}
